import * as React from "react";

import "./imageFieldCreator.scss";
import "../customize-panel.scss";

import {cs}                  from "@Common/react/chain-services";
import {keyed}               from "@Common/react/keyed";
import {consumeContext}      from "@Common/react/context";
import {Load2}               from "@Common/react/load2";
import {AlertModal}          from "@Common/ui-components/alert-modal/alert-modal";
import {GetImageQuality}     from "@Common/ui-components/photo-dashboard/utils/get-image-quality";
import {downloadArrayBuffer} from "@Common/utils/browser/download-array-buffer";
import {bytesToUnitString}   from "@Common/utils/math/bytes-to-unit-string";

import {BlobToFile}          from "@Xpresso/routes/account/common/blob-to-file";
import {ImageAssetEditModal} from "@Xpresso/routes/account/common/image-asset-edit-modal/image-asset-edit-modal";
import {UseState}       from "@Common/react/use-state";
import { SaveAlerts } from "../../../common/SaveAlerts";
import {TimeoutAction}        from "@Common/react/timeout-action";

export const ImageFieldCreator = ({orderId, data, imageType, updateImage, onApplyImageFromPhotoDashboard, showGallery, allowedGalleryTabs, editable, setUnsavedChanges, xdQaName}) => {

    function createFields(imageField, index) {
        const idPrimary = imageField.idPrimary;
        const fileSizeString = bytesToUnitString(imageField.data?.fileSize, 1);
        return cs(
            keyed(idPrimary),
            consumeContext("apis"),
            consumeContext("session"),
            consumeContext("company"),
            consumeContext("userLevels"),
            consumeContext("systemConstants"),
            consumeContext("templateOptions"),
            ["agent", ({session}, next) => next(session?.data?.agent)],
            ({agent}, next) => !agent ? "Loading agent..." : next(),
            ["imageType", (_, next) => next(imageType)],
            ["saveContentAlert", (_, next) => UseState({next})],
            ["usersHeadshots", ({apis}, next) => Load2({
                fetch: () => imageType === "headshot" && apis.next.gallery.headshots.getImages(),
                captureException: true,
                next,
            })],
            ["usersLogos", ({apis}, next) => Load2({
                fetch: () => imageType === "logo" && apis.next.gallery.logos.getImages(),
                captureException: true,
                next,
            })],
            ["usersDesignations", ({apis, templateOptions}, next) => Load2({
                fetch: () => imageType === "designation" && apis.next.gallery.designations.getImages(parseInt(data[0].idTemplate), {isBuild3Order: true}),
                captureException: true,
                next,
            })],
            ["allowLowQualityPhotos", ({company}, next) => next( {
                "headshot": company.prefs.allowLowQualityPhotosHeadshot,
                "photo": company.prefs.allowLowQualityPhotosProperty,
                "qrCode": company.prefs.allowLowQualityPhotosProperty,
                "logo": company.prefs.allowLowQualityPhotosLogo
            } )],
            ["LowQualityImageAlert", ({}, next) => AlertModal({
                next,
                header: "Your image edit was not saved",
                message: "The edits you made resulted in an image that does not meet the minimum quality standards for our printing process.",
                cancelButton: false,
                xdQaName: "LowQualityImageAlert",
            })],
            ["deleteImage", ({apis, agent}, next) => next( async () => {
                if (confirm(`Delete ${imageType}?`)) {
                    if (imageType == "headshot") {
                        await apis.next.gallery.headshots.delete(imageField.data.idPrimary, {agentId: agent.idAgent});
                    }
                    else if (imageType == "logo") {
                        await apis.next.gallery.logos.delete(imageField.data.idPrimary, {agentId: agent.idAgent});
                    }
                    else if (imageType == "photo" || imageType == "qrCode") {
                        await apis.next.gallery.orders.delete({
                            orderId,
                            imageId: imageField.data.idPrimary,
                        })
                    }
                    await updateImage(orderId, {[idPrimary] : "''"});
                }
            })],
            ["editModal", ({apis, agent, deleteImage, company, templateOptions, allowLowQualityPhotos, LowQualityImageAlert, saveContentAlert}, next) => ImageAssetEditModal({
                agentId: agent.idAgent,
                onEdit: async ({id, data}) => {
                    if (imageType == "headshot") {
                        await apis.next.gallery.headshots.update({
                            id,
                            postData: data,
                            agentId: agent.idAgent,
                        });
                    }
                    if (imageType == "logo") {
                        await apis.next.gallery.logos.update({
                            id,
                            postData: data,
                            agentId: agent.idAgent,
                        });
                    }
                    if (imageType == "photo") {
                        await apis.next.gallery.orders.update({
                            orderId,
                            imageId: id,
                            postData: {
                                name: data.name
                            }
                        })
                    }
                    await updateImage(orderId, {[idPrimary] : id});
                },
                onImageEdit: async ({mimeString, name: suggestedName, content, defaultSlot}) => {
                    const name = await (async () => {
                        if (imageType == "headshot") {
                            let headshots = await apis.next.gallery.headshots.getImages({agentId: agent.idAgent});
                            for (let i = 0; ; i++) {
                                const proposedName = suggestedName + (i === 0 ? "" : `(${i})`);
                                if (!headshots.some((hs) => hs.name === proposedName)) {
                                    return proposedName;
                                }
                            }
                        }
                        if (imageType == "logo") {
                            let logos = await apis.next.gallery.logos.getImages({agentId: agent.idAgent});
                            for (let i = 0; ; i++) {
                                const proposedName = suggestedName + (i === 0 ? "" : `(${i})`);
                                if (!logos.some((logo) => logo.name === proposedName)) {
                                    return proposedName;
                                }
                            }
                        }
                        if (imageType == "photo") {
                            for (let i = 0; ; i++) {
                                const proposedName = suggestedName + (i === 0 ? "" : `(${i})`);
                                if (!data.some((photo) => photo.data?.name === proposedName)) {
                                    return proposedName;
                                }
                            }
                        }
                    })();

                    if (imageType == "headshot") {
                        const result = await apis.next.gallery.headshots.upload({
                            agent: agent.idAgent,
                            formData: {
                                file: BlobToFile({
                                    name: name + ".png",
                                    data: content,
                                    mimeString,
                                }),
                                name,
                                defaultSlot: defaultSlot
                            },
                        });
                        const quality = GetImageQuality({
                            imageInfo: result,
                            photoQualitySettings: templateOptions.photoQualitySettings
                        });
                        if (quality?.label === "Low" && allowLowQualityPhotos[imageType] !== true) {
                            await apis.next.gallery.headshots.delete(result.id, {agentId: agent.idAgent});
                            LowQualityImageAlert.show();
                            return;
                        }
                        if (result.errorMessage) {
                            saveContentAlert.onChange({
                                heading: "Error",
                                message: result.errorMessage,
                                success: false,
                            });
                        } else {
                            await updateImage(orderId, {[idPrimary] : result.id});
                        }
                    }
                    if (imageType == "logo") {
                        const result = await apis.next.gallery.logos.upload({
                            formData: {
                                file: BlobToFile({
                                    name: name + ".png",
                                    data: content,
                                    mimeString,
                                }),
                                name,
                                idEntity: agent.idAgent,
                                defaultSlot: defaultSlot
                            },
                        });
                        const quality = GetImageQuality({
                            imageInfo: result,
                            photoQualitySettings: templateOptions.photoQualitySettings
                        });
                        if (quality?.label === "Low" && allowLowQualityPhotos[imageType] !== true) {
                            await apis.next.gallery.logos.delete(result.id, {agentId: agent.idAgent});
                            LowQualityImageAlert.show();
                            return;
                        }
                        if (result.errorMessage) {
                            saveContentAlert.onChange({
                                heading: "Error",
                                message: result.errorMessage,
                                success: false,
                            });
                        } else {
                            await updateImage(orderId, {[idPrimary] : result.id});
                        }
                    }
                    if (imageType == "photo" || imageType == "qrCode") {
                        const result = await apis.next.gallery.orders.upload({
                            orderId,
                            formData: {
                                file: BlobToFile({
                                    name: name + ".png",
                                    data: content,
                                    mimeString,
                                }),
                                name,
                            },
                        });
                        const quality = GetImageQuality({
                            imageInfo: result,
                            photoQualitySettings: templateOptions.photoQualitySettings
                        });
                        if (quality?.label === "Low" && allowLowQualityPhotos[imageType] !== true) {
                            await apis.next.gallery.orders.delete({
                                orderId,
                                imageId: result.id,
                            });
                            LowQualityImageAlert.show();
                            return;
                        }
                        if (result.errorMessage) {
                            saveContentAlert.onChange({
                                heading: "Error",
                                message: result.errorMessage,
                                success: false,
                            });
                        } else {
                            await updateImage(orderId, {[idPrimary] : result.id});
                        }
                    }
                },
                onDelete: async (modal) => {
                    deleteImage();
                    modal.close();
                },
                build3Customize: true,
                next,
            })],
            ["displayModal", ({editModal}, next) => next(() =>
                editModal.show({
                    data: {
                        ...imageField.data,
                        thumbnailUrl: imageField.data.thumbnail,
                        quality: imageField.data.photoQuality,
                        defaultSlot: 1,
                        width: 333,
                        height: 500,
                        printUrl: imageField.data.imageUrl,
                        id: imageField.data.idPrimary
                    },
                    type: imageType
                })
            )],
            ["missingGalleriesModal", ({}, next) => AlertModal({
                next,
                header: "No photo gallery options currently available on this template",
                message: <>
                        <p>Please email or call our support team</p>
                        <p>
                            Support Email: <span className="info-span">info@xpressdocs.com</span><br/>
                            Support Phone: <span className="info-span">(866) 977-3627</span>
                        </p>
                    </>,
                cancelButton: false,
                xdQaName: "missingGalleryModal",
            })],

            ({deleteImage, displayModal, templateOptions, missingGalleriesModal, usersHeadshots, usersLogos, usersDesignations, imageType, saveContentAlert}) => (
                <div className="image-field-creator-b33" key={imageField.idPrimary} xd-qa-type="card" xd-qa-name={imageField.label}>
                    {saveContentAlert.value && <>
                    {SaveAlerts({
                        message: saveContentAlert.value,
                        onClose: () => saveContentAlert.onChange(null),
                    })}

                    {saveContentAlert.value.success && TimeoutAction({
                        delay: 4000,
                        action: () => saveContentAlert.onChange(null),
                    })}
                </>}
                    {imageType === "designation" ?
                        <h3 className="customization-tile-title" xd-qa-type="label" xd-qa-name={"Designation " + (imageField.label.match(/\d+/)[0])}>{"Designation " + (imageField.label.match(/\d+/)[0]) + ":"}</h3>
                    :
                        <h3 className="customization-tile-title" xd-qa-type="label" xd-qa-name={imageField.label}>{imageField.label}</h3>
                    }
                    {imageField.data ?
                    <>
                        <div className="image-field-card">
                            <div className="image-field-photo">
                                {imageField.data?.thumbnail && <img src={imageField.data.thumbnail} xd-qa-type="image" xd-qa-name="Image Field Photo"/>}
                            </div>
                            <div className="image-field-information">
                                <div className="image-field-description">
                                    <div className="image-name-and-file-size">
                                        <p title={imageField.data?.name} className="image-field-photo-name" xd-qa-type="label" xd-qa-name="Image Field Photo Name">
                                            {imageField.data?.name}
                                        </p>
                                        <p className="image-field-file-size" xd-qa-type="label" xd-qa-name="Image Field File Size">{"File Size: " + fileSizeString}</p>
                                    </div>
                                    {imageType !="designation" ? <div className={`image-field-photo-quality ${imageField.data?.photoQuality?.toString()?.toLowerCase()}`}>
                                        <p xd-qa-type="label" xd-qa-name="image field photo Quality">{imageField.data.photoQuality + " Quality"}</p>
                                    </div> : null}
                                </div>
                                { imageType !="designation" &&
                                <div className="image-field-editcontrols-cont">
                                    { (editable || imageField.idContent == "headshot_02") &&
                                        <><span className="link-span" xd-qa-type="link" xd-qa-name={`Edit Image ${imageField.idContent}`} onClick={ () => {displayModal();} }>Edit Image</span><span className="pipe-char174"> | </span></>
                                    }
                                    <span className="link-span" xd-qa-type="link" xd-qa-name={`Download ${imageField.idContent}`} onClick={() =>
                                            fetch(imageField.data.imageUrl, {
                                                method: "GET",
                                            }).then(response => {
                                                response.arrayBuffer().then(function(buffer) {
                                                    downloadArrayBuffer(buffer, {fileName: (imageField.data.name || imageField.data.idPrimary) + "." + imageField.data.fileType});
                                                });
                                            }).catch(() => {
                                                saveContentAlert.onChange({
                                                    heading: "Error",
                                                    message: "Error: Could not create image download.",
                                                    success: false,
                                                });
                                            })
                                        }>Download</span>
                                    { editable && <><span className="pipe-char174"> | </span><span className="link-span" xd-qa-type="link" xd-qa-name={`Delete ${imageField.idContent}`} onClick={deleteImage}>Delete</span></> }
                                </div>}
                                <div className="image-field-button">
                                    <span
                                        onClick={async () => {
                                            setUnsavedChanges();
                                            return await updateImage(orderId, {[idPrimary] : "''"})
                                        }}
                                        xd-qa-type="link" xd-qa-name={`REMOVE ${imageField.idContent}`}>REMOVE</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            showGallery({
                                                imageArea: {
                                                    allowedTabs: allowedGalleryTabs,
                                                    name: idPrimary,
                                                    photoQualitySettings: {
                                                        ...templateOptions.photoQualitySettings,
                                                        forHeadshotOrLogo: imageField.idContent.startsWith("headshot") || imageField.idContent.startsWith("logo")
                                                    },
                                                },
                                                templateId: data[0].idTemplate,
                                                allowUploadOverride: imageField.idContent === "headshot_02",
                                                disallowSvgImages: true,
                                                onApplyImage: onApplyImageFromPhotoDashboard,
                                            })
                                        }}
                                        xd-qa-type="button"
                                        xd-qa-name={`Choose Another Image ${imageField.idContent}`}
                                    >CHOOSE ANOTHER IMAGE</button>
                                </div>
                            </div>
                        </div>
                    </> :
                    <div className="image-field-empty-photo-card">
                        { imageType == "headshot" ?
                            (!editable && imageField.idContent !== "headshot_02" && !usersHeadshots.value?.length) ?
                                <p className="no-permission" xd-qa-type="label" xd-qa-name="No headshot selected">No headshot available for your account.<br/> Please work with your admin to have a headshot added to your account.</p>
                            :
                            <>
                                <p xd-qa-type="label" xd-qa-name="No headshot selected">No headshot selected.</p>
                                <p><span className="link-span"
                                xd-qa-type="link" xd-qa-name="Click Here to select headshot for this position"
                                    onClick={() => {
                                        showGallery({
                                            imageArea: {
                                                allowedTabs: allowedGalleryTabs,
                                                name: idPrimary,
                                                photoQualitySettings: {
                                                    ...templateOptions.photoQualitySettings,
                                                    forHeadshotOrLogo: true
                                                },
                                            },
                                            allowUploadOverride: imageField.idContent === "headshot_02",
                                            disallowSvgImages: true,
                                            onApplyImage: onApplyImageFromPhotoDashboard,
                                        })
                                    }}
                                >Click Here</span> to select headshot for this position.</p>
                            </> : imageType == "photo" ?
                            <>
                                <p xd-qa-type="label" xd-qa-name="Your Photo Gallery Is Empty">This image field is empty.</p>
                                <p><span className="link-span" xd-qa-type="link" xd-qa-name="Click Here to insert a photo"
                                    onClick={() => {
                                        if (!allowedGalleryTabs.length) {
                                            missingGalleriesModal.show()
                                        } else {
                                            showGallery({
                                                imageArea: {
                                                    allowedTabs: allowedGalleryTabs,
                                                    name: idPrimary,
                                                    photoQualitySettings: templateOptions.photoQualitySettings,
                                                },
                                                disallowSvgImages: true,
                                                onApplyImage: onApplyImageFromPhotoDashboard,
                                            })
                                        }
                                    }}
                                >Click Here</span> to add a photo.</p>
                            </> : imageType == "logo" || imageType == "designation"?
                            (!editable && !usersLogos.value?.length && !usersDesignations.value?.length) ?
                                <p className="no-permission" xd-qa-type="label" xd-qa-name="No logo selected">Your logo gallery is empty.<br/> Please work with your admin to have logos added to your gallery.</p>
                            :
                            <>
                                <p xd-qa-type="label" xd-qa-name="Your Logo Gallery Is Empty">This {imageType} field is empty.</p>
                                <p><span className="link-span" xd-qa-type="link" xd-qa-name="Click Here to insert a photo"
                                    onClick={() => {
                                        showGallery({
                                            imageArea: {
                                                allowedTabs: allowedGalleryTabs,
                                                name: idPrimary,
                                                photoQualitySettings: {
                                                    ...templateOptions.photoQualitySettings,
                                                    forHeadshotOrLogo: true
                                                },
                                            },
                                            templateId: data[0].idTemplate,
                                            disallowSvgImages: true,
                                            onApplyImage: onApplyImageFromPhotoDashboard,
                                        })
                                    }}
                                >Click Here</span> to add {imageType}.</p>
                            </> :
                            <>
                                <p xd-qa-type="label" xd-qa-name="No QR Code selected">This QR Code field is empty.</p>
                                <p><span className="link-span" xd-qa-type="link" xd-qa-name="Click Here to insert a QR Code"
                                    onClick={() => {
                                        if (!allowedGalleryTabs.length) {
                                            missingGalleriesModal.show()
                                        } else {
                                            showGallery({
                                                imageArea: {
                                                    allowedTabs: allowedGalleryTabs,
                                                    name: idPrimary,
                                                    photoQualitySettings: templateOptions.photoQualitySettings,
                                                },
                                                disallowSvgImages: true,
                                                onApplyImage: onApplyImageFromPhotoDashboard,
                                            })
                                        }
                                    }}
                                >Click Here</span> to add QR Codes.</p>
                            </>
                        }
                    </div>
                    }
                    {data.indexOf(imageField) < data.length-1 && <hr />}
                </div>
            )
        )
    }

    return data.map(createFields);

}
